/**
 * Dev-tunnel detection, kept free of any expo/react-native imports so it can
 * be unit-tested in the node/vitest environment (importing lib/constants.ts
 * directly drags in expo-constants → expo-modules-core, which needs a native
 * or web global runtime).
 *
 * The rule is provider-agnostic: when a DEV web bundle is served from any
 * non-localhost host — an ngrok/cloudflared https tunnel, a LAN IP on a
 * phone — the API cannot be this machine's localhost (other devices can't
 * resolve it, and https pages block plain-http fetches as mixed content).
 * scripts/dev-tunnel-proxy.mjs serves the app and routes /api/* to the local
 * API on ONE port, so same-origin is always the right target there.
 * Production never hits this: the caller additionally gates on __DEV__, and
 * release builds get the API URL injected upstream of the fallback chain.
 */
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '[::1]'])

/**
 * The ONE safe way to read the page hostname. Native release builds define
 * `window` but not `window.location`, so `window.location.hostname` throws
 * there (it crashed the signed-in home screen on device). Dev builds and Expo
 * Go DO expose `window.location`, which is why the bug only showed up in
 * release builds. Never read `window.location.hostname` anywhere else.
 */
export function getWindowHostname(
  win: { location?: { hostname?: string } } | undefined = typeof window !== 'undefined'
    ? window
    : undefined,
): string | undefined {
  return win?.location?.hostname
}

/**
 * True only for a known local host. `undefined` (no `window.location`, as on
 * native release builds) is NOT local.
 */
export function isLocalHost(hostname: string | undefined): boolean {
  return !!hostname && LOCAL_HOSTNAMES.has(hostname.toLowerCase())
}

/** Dev builds and localhost web builds may use local-only QA affordances. */
export function isLocalQaEnvironment(
  isDev: boolean = typeof __DEV__ !== 'undefined' && __DEV__,
  hostname: string | undefined = getWindowHostname(),
): boolean {
  return isDev || isLocalHost(hostname)
}

export function isRemoteDevHost(hostname: string | undefined): boolean {
  if (!hostname) return false
  return !LOCAL_HOSTNAMES.has(hostname.toLowerCase())
}

/**
 * Same-origin routing is only valid for a browser bundle. Expo Go also exposes
 * a `window.location` whose host is Metro's LAN address; treating that as web
 * sends native API requests to Metro, which answers with HTML instead of JSON.
 */
export function shouldUseSameOriginDevApi(
  platform: string | undefined,
  hostname: string | undefined,
): boolean {
  return platform === 'web' && isRemoteDevHost(hostname)
}
